import en from './locales/en.js'
import nl from './locales/nl.js'
import fr from './locales/fr.js'
import it from './locales/it.js'
import ptBr from './locales/pt-br.js'
import zhCn from './locales/zh-cn.js'
import zhTw from './locales/zh-tw.js'
import th from './locales/th.js'
import vi from './locales/vi.js'

// ── Adding a language ─────────────────────────────────────────────────────
// 1. Copy src/i18n/locales/en.js to <code>.js (e.g. de.js) and translate the
//    values. Keys you omit fall back to English automatically.
// 2. Import it above and add it to the map below. That's it — the host passes
//    ?locale=de (or the embedder sets `locale` in the plugin config) and the
//    widget picks it up.
// Never translate the product name "RPilot" — it's a brand.
//
// Map keys are lowercase; regional variants use hyphens ("pt-br"). Bare
// language keys double as aliases so "pt-PT" still lands on Portuguese and
// "zh" defaults to Simplified. English variants (en-AU, en-GB) all resolve to
// the single `en` file, which is written in British English.
const locales = {
  en,
  nl,
  fr,
  it,
  'pt-br': ptBr,
  pt: ptBr,
  'zh-cn': zhCn,
  'zh-tw': zhTw,
  zh: zhCn,
  th,
  vi,
}

export const DEFAULT_LOCALE = 'en'

/**
 * Normalize a host-supplied locale to a registered one: exact match first
 * ("zh-TW" / "zh_TW" → "zh-tw"), then the language part of a regional tag
 * ("nl-BE" → "nl"), else the default. Case-insensitive.
 */
export function resolveLocale(locale) {
  if (!locale) return DEFAULT_LOCALE
  const lc = String(locale).trim().toLowerCase().replace(/_/g, '-')
  if (locales[lc]) return lc
  const base = lc.split('-')[0]
  return locales[base] ? base : DEFAULT_LOCALE
}

/**
 * Build a translate function bound to one locale. `t(key, params)` looks the
 * key up in the locale, falls back to English, and finally to the key itself
 * (so a typo'd key is visible, not invisible). `params` interpolates
 * `{name}`-style placeholders.
 */
export function createT(locale) {
  const messages = locales[resolveLocale(locale)]
  return function t(key, params) {
    let s = messages[key] ?? en[key] ?? key
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        s = s.split(`{${k}}`).join(String(v))
      }
    }
    return s
  }
}
